import type { ChangeEvent, RefObject } from "react";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import type { SearchHistoryType } from "~/components/blocks/layout/global-navi/util/search-history-manipulator";
import {
  removeSearchHistory,
  appendSearchHistory,
  getSearchHistories,
} from "~/components/blocks/layout/global-navi/util/search-history-manipulator";
import { runningInBrowser } from "~/const/browser";
import type { ListSearchQuery } from "~/const/hrefs";
import { PATHS } from "~/const/paths";
import { useQueryString } from "~/hooks/use-query-string";
import { useRouterForVodList } from "~/hooks/use-router-for-vod-list";
import { sendTrackingClickCommon } from "~/lib/tracking/events";
import { useAuth } from "~/provider/auth";

let timerId: number;

let isShowHistoryHold = false;

type UseSearchFormProps = {
  inputRef: RefObject<HTMLLabelElement>;
  onVisibleHistoryList: () => void;
  onHideHistoryList: () => void;
};

/**
 * グロナビの検索フォームに関連した処理
 */
export const useDesktopSearchForm = (props: UseSearchFormProps) => {
  const { inputRef, onVisibleHistoryList, onHideHistoryList } = props;
  const { asPath } = useRouter();
  const { pushToVodListWithoutProductModal } = useRouterForVodList();
  const query = useQueryString();
  const { isLoggedIn, userId } = useAuth();
  const [keyword, setKeyword] = useState("");
  const [searchHistories, setSearchHistories] = useState(isLoggedIn ? getSearchHistories(userId) : []);
  const isRunningLazySearch = useRef(false);

  const isVodGravureTop = asPath.startsWith(PATHS.VOD_GRAVURE);

  const pushToVideoList = async ({ keyword, type }: SearchHistoryType) => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { keyword: _, ...rest } = query;
    const isGravure = type === "gravure";
    const newQuery: ListSearchQuery =
      keyword === ""
        ? rest
        : {
            ...rest,
            keyword,
          };

    await pushToVodListWithoutProductModal({ query: newQuery, isGravure });

    isRunningLazySearch.current = false;
  };

  const onClickClearSearchHistory = (index: number) => {
    removeSearchHistory(index, userId);
    updateSearchHistoryState();
  };

  const onFocusSearchForm = () => {
    if (isShowHistoryHold) {
      isShowHistoryHold = false;
    }
    if (keyword === "") {
      onVisibleHistoryList();
    }
  };

  const onBlurSearchForm = () => {
    if (isShowHistoryHold) return;
    onHideHistoryList();

    if (!isLoggedIn) return;
    appendSearchHistory({ keyword, type: asPath.includes(PATHS.VOD_GRAVURE) ? "gravure" : undefined }, userId);
    updateSearchHistoryState();
  };

  const onMouseDownSuggestArea = (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A") return;
    // Blurの無効化用
    e.preventDefault();
  };

  const onChangeInputValue = (e: ChangeEvent<HTMLInputElement>) => {
    isRunningLazySearch.current = true;
    const { value } = e.currentTarget;
    setKeyword(value);

    const trimmedValue = value.trim();

    // MEMO（kimura）: 先頭と末尾の空白文字を削除した検索文字列が空の場合に「検索候補」を表示する
    if (trimmedValue === "") {
      onVisibleHistoryList();
    } else {
      onHideHistoryList();
      pushToSearchLazily(value);
    }
  };

  const pushToSearchLazily = (value: string) => {
    if (!runningInBrowser) return;
    clearTimeout(timerId);
    timerId = window.setTimeout(() => {
      const trimmedValue = value.trim();

      // MEMO（kimura）: 文字列の両端の空白を削除した上で空文字でない場合のみ検索履歴を隠す
      if (trimmedValue !== "") {
        onHideHistoryList();
      }

      sendTrackingClickCommon({ clickEventName: "dmmtv_button_search", viaInfo: "search", misc: keyword });

      // MEMO（kimura）: 空白を削除した文字列（trimmedValue）で検索は実行しない。理由は検索実行後にtrimされるから。
      pushToVideoList({ keyword: value, type: isVodGravureTop ? "gravure" : undefined });
    }, 500);
  };

  const onHoldOff = () => {
    onHideHistoryList();
    isShowHistoryHold = false;
  };

  const onClickClearButton = () => {
    setKeyword("");
    pushToVideoList({ keyword: "", type: isVodGravureTop ? "gravure" : undefined });
    onVisibleHistoryList();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    // Enterキーによるサブミット防止
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const updateSearchHistoryState = () => {
    setSearchHistories(getSearchHistories(userId));
  };

  /** keyword query を監視する副作用処理 */
  const queryParamsListener = () => {
    if (isRunningLazySearch.current) return;
    setKeyword(query?.keyword ?? "");
  };

  /** 検索フォームでtab押下時履歴リスト表示ホールド */
  const holdHistoryList = () => {
    const targetElement = inputRef.current;
    if (!targetElement) return;
    const inputElement = targetElement.querySelector("input") as HTMLInputElement;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        isShowHistoryHold = true;
      }
      if (e.key === "Escape") {
        onHoldOff();
        setKeyword("");
        inputElement.blur();
        isShowHistoryHold = false;
      }
    };
    inputElement.addEventListener("keydown", onKeyDown, { passive: true });
    return () => {
      isShowHistoryHold = false;
      inputElement.removeEventListener("keydown", onKeyDown);
    };
  };

  useEffect(queryParamsListener, [query]);
  useEffect(holdHistoryList, []);

  return {
    onClickClearSearchHistory,
    onChangeInputValue,
    onClickClearButton,
    onFocusSearchForm,
    onBlurSearchForm,
    onMouseDownSuggestArea,
    onKeyDown,
    pushToVideoList,
    onHoldOff,
    keyword,
    searchHistories,
  };
};
